// Phone input (+977), DESIGN_SYSTEM §9.3. A fixed prefix chip and the 10 digits as typed, with no
// spaces (owner, 2026-10-21: "9826355355", not "982 635 5355"); the value handed back is E.164.
// Pasted "+977-98…", "977…" and Devanagari digits normalise to the same value. Validation messages
// come from `phoneError`.
import { forwardRef, useState, type ChangeEvent } from "react";
import { normalizeDigits, parseNepalMobile, type PhoneParseResult } from "@ady/format";
import { t } from "@ady/i18n";
import { Input, type InputProps } from "./Input";

export interface PhoneInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "prefix" | "type"
> {
  /** E.164 (`+9779851234567`) or "" */
  value: string;
  onValueChange: (e164: string, result: PhoneParseResult) => void;
}

function nationalFromE164(v: string): string {
  return v.startsWith("+977") ? v.slice(4) : v;
}

/** Extracts the national digits from anything typed or pasted. */
export function phoneDigits(raw: string): string {
  let d = normalizeDigits(raw).replace(/\D/g, "");
  if (d.startsWith("00977")) d = d.slice(5);
  // Only a full 13-digit "977…" is a country code: 97x is also a valid mobile prefix.
  else if (d.startsWith("977") && d.length >= 13) d = d.slice(3);
  return d.slice(0, 10);
}

/** The catalogue message for a failed parse, or undefined when valid. */
export function phoneError(result: PhoneParseResult): string | undefined {
  return result.ok ? undefined : t(`phone.errors.${result.error}`);
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onValueChange, ...rest },
  ref,
) {
  const [digits, setDigits] = useState(() => nationalFromE164(value));
  // Re-sync when the parent replaces the value (e.g. form reset).
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    if (nationalFromE164(value) !== digits) setDigits(nationalFromE164(value));
  }
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = phoneDigits(e.target.value);
    setDigits(next);
    const result = parseNepalMobile(next);
    onValueChange(result.ok ? result.e164 : "", result);
  };
  return (
    <Input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      prefix={t("phone.prefix")}
      prefixLabel={t("phone.prefixLabel")}
      value={digits}
      onChange={handleChange}
      ledger
      {...rest}
    />
  );
});
